import { compareAsc, parseISO } from 'date-fns';

import { Api } from '../../api';
import { capitalize } from '../../util';

export type WishlistColumn =
  | 'idea'
  | 'preparing'
  | 'service_order'
  | 'cancelled';

/** A column an idea can be moved onto the board into by hand. */
export type MoveDestination = 'idea' | 'preparing';

export type Idea = Api.CompanyWishlist.V['ideas'][number];
export type ServiceOrderGroup = Api.CompanyWishlist.V['serviceOrders'][number];

export const COLUMNS: ReadonlyArray<{
  key: WishlistColumn;
  label: string;
}> = [
  { key: 'idea', label: 'Ideas' },
  { key: 'preparing', label: 'Preparing' },
  { key: 'service_order', label: 'Service Orders' },
  { key: 'cancelled', label: 'Cancelled' },
];

export const EMPTY_COPY: Record<WishlistColumn, string> = {
  idea: 'No ideas yet — add the first one.',
  preparing: 'Nothing in Preparing yet.',
  service_order: 'No service orders linked yet.',
  cancelled: 'Nothing cancelled.',
};

export function labelForColumn(column: string): string {
  const match = COLUMNS.find((c) => c.key === column);
  return match ? match.label : column;
}

/**
 * Reordering is scoped to the columns a role can act in: clients reorder
 * within Ideas, agency users within Ideas and Preparing.
 */
export function canReorderColumn(
  column: WishlistColumn,
  agency: boolean
): boolean {
  if (column === 'idea') return true;
  if (column === 'preparing') return agency;
  return false;
}

export function ideasForColumn(
  ideas: ReadonlyArray<Idea>,
  column: WishlistColumn
): Array<Idea> {
  return ideas
    .filter((idea) => idea.state === column)
    .sort(
      (a, b) =>
        a.position - b.position ||
        compareAsc(parseISO(a.createdAt), parseISO(b.createdAt))
    );
}

export function serviceOrderIdeas(
  ideas: ReadonlyArray<Idea>,
  order: ServiceOrderGroup
): Array<Idea> {
  return order.ideaIds
    .map((iri) => ideas.find((idea) => idea['@id'] === iri))
    .filter((idea): idea is Idea => idea !== undefined);
}

export function serviceOrderStatusLabel(status: string | null): string {
  if (!status) return 'Unknown';
  return capitalize(status.replace(/_/g, ' '));
}

/**
 * The move call that puts an idea back on the board: reopening a cancelled
 * idea or unlinking one from its service order both land it in Ideas or
 * Preparing.
 */
export function moveIdeaRequest(ideaId: string, state: MoveDestination) {
  return {
    path: `/wishlist-ideas/${ideaId}/move`,
    options: { method: 'POST' as const, jsonBody: { state } },
  };
}

/**
 * The position to persist for an idea dropped at `index` in a column: the
 * midpoint between its new neighbours, matching the backend's float ordering.
 */
export function positionForDrop(
  column: ReadonlyArray<Idea>,
  index: number
): number {
  const prev: Idea | undefined = column[index - 1];
  const next: Idea | undefined = column[index + 1];
  if (prev && next) return (prev.position + next.position) / 2;
  if (next) return next.position / 2;
  if (prev) return prev.position + 1;
  return 0;
}
